import { Injectable } from "@angular/core";
import { ArrayHelper } from "@calaosoft/osapp-common/utils/helpers/arrayHelper";
import { ITaskDescriptor as IOldTaskDescriptor } from "../../../services/backgroundTask/itask-descriptor";
import { FlagService } from "../../../services/flag.service";
import { SqlService } from "../../sqlite/services/sql.service";
import { ISerializableTaskDescriptor } from "../models/iserializable-task-descriptor";
import { ITaskDescriptor } from "../models/itask-descriptor";
import { BackgroundTaskDatabaseService } from "./background-task-database.service";

type TTaskDescriptor = ITaskDescriptor | IOldTaskDescriptor;

enum ETransactionAction {
	delete,
	deleteMultiple,
	put
}

@Injectable()
export class BrowserBackgroundTaskDatabaseService extends BackgroundTaskDatabaseService<IDBDatabase> {
	//#region FIELDS

	protected static override readonly C_LOG_ID = "BRWSR.BTS.DB.S::";

	//#endregion FIELDS

	//#region METHODS

	public constructor(psvcSql: SqlService, psvcFlag: FlagService) {
		super(psvcSql, psvcFlag);
	}

	private async getNewTransactionAsync(psMode: IDBTransactionMode): Promise<IDBTransaction> {
		const loDatabase: IDBDatabase = await this.moDatabaseAsync;

		return loDatabase.transaction(BrowserBackgroundTaskDatabaseService.C_DESCRIPTOR_TABLE_NAME, psMode);
	}

	private transactAsync(peAction: ETransactionAction.delete, poDocumentOrId: TTaskDescriptor | string): Promise<void>;
	private transactAsync(
		peAction: ETransactionAction.deleteMultiple,
		paDocumentIds: TTaskDescriptor[] | string[]
	): Promise<void>;
	private transactAsync(peAction: ETransactionAction.put, poDocument: TTaskDescriptor): Promise<void>;
	private async transactAsync(
		peAction: ETransactionAction,
		poData: TTaskDescriptor | string | TTaskDescriptor[] | string[]
	): Promise<void> {
		const loTransaction: IDBTransaction = await this.getNewTransactionAsync("readwrite");
		const laJoinedDocsInfo: string = this.getDocumentInfo(poData).join(", ");

		console.info(
			`${BrowserBackgroundTaskDatabaseService.C_LOG_ID}${peAction} task descriptors '[${laJoinedDocsInfo}]' begins`
		);

		return new Promise<void>((pfResolve, pfReject) => {
			loTransaction.onerror = (poEvent: Event) => {
				console.error(
					`${BrowserBackgroundTaskDatabaseService.C_LOG_ID}Error ${peAction} task descriptors '[${laJoinedDocsInfo}]'`,
					(poEvent.target as IDBTransaction).error
				);
				pfReject((poEvent.target as IDBTransaction).error);
			};

			loTransaction.oncomplete = () => {
				console.info(
					`${BrowserBackgroundTaskDatabaseService.C_LOG_ID}${peAction} task descriptors '[${laJoinedDocsInfo}]' ended`
				);
				pfResolve();
			};

			const loStore: IDBObjectStore = loTransaction.objectStore(
				BrowserBackgroundTaskDatabaseService.C_DESCRIPTOR_TABLE_NAME
			);

			try {
				this.execTransaction(peAction, poData, loStore);
			} catch (poError) {
				console.error(
					`${BrowserBackgroundTaskDatabaseService.C_LOG_ID}Error while executing transaction for action '${peAction}' :`,
					poError
				);
				pfReject(poError);
			}
		});
	}

	/** Récupère les informations de document à partir des données fournies (identifiant unique et description).
	 * @param poData Données dont il faut récupérer les informations de document.
	 */
	private getDocumentInfo(poData: TTaskDescriptor | string | TTaskDescriptor[] | string[]): string[] {
		if (poData instanceof Array) {
			if (typeof ArrayHelper.getFirstElement(poData as string[]) === "string") return poData as string[];
			else {
				return (poData as TTaskDescriptor[]).map(
					(poDoc: TTaskDescriptor) =>
						`${(poDoc as ITaskDescriptor).uid ?? (poDoc as IOldTaskDescriptor)._id} (${(poDoc as ITaskDescriptor).description ?? (poDoc as IOldTaskDescriptor).name})`
				);
			}
		} else {
			if (typeof poData === "string") return [poData];
			else {
				return [
					`${(poData as ITaskDescriptor).uid ?? (poData as IOldTaskDescriptor)._id} (${(poData as ITaskDescriptor).description ?? (poData as IOldTaskDescriptor).name})`
				];
			}
		}
	}

	private execTransaction(
		peAction: ETransactionAction,
		poData: TTaskDescriptor | string | TTaskDescriptor[] | string[],
		poStore: IDBObjectStore
	): void {
		switch (peAction) {
			case ETransactionAction.delete: // Suppression unitaire.
				poStore.delete(this.getDocumentIds(poData)[0]);
				break;

			case ETransactionAction.deleteMultiple: // On doit convertir le "ITaskDescriptor" en "ISerializableTaskDescriptor".
				this.getDocumentIds(poData).forEach((psId: string) => poStore.delete(psId));
				break;

			case ETransactionAction.put:
				if (typeof poData === "string" || poData instanceof Array)
					throw new Error("Cannot put because data is not a task descriptor !");
				else poStore.put(this.toSerializableTaskDescriptor(poData));
				break;

			default:
				throw new Error("Unknown transaction action");
		}
	}

	private getDocumentIds(poData: TTaskDescriptor | string | TTaskDescriptor[] | string[]): string[] {
		if (poData instanceof Array) {
			if (typeof ArrayHelper.getFirstElement(poData as string[]) === "string") return poData as string[];
			else {
				return (poData as TTaskDescriptor[]).map(
					(poDoc: TTaskDescriptor) => (poDoc as ITaskDescriptor).uid ?? (poDoc as IOldTaskDescriptor)._id
				);
			}
		} else {
			return typeof poData === "string"
				? [poData]
				: [(poData as ITaskDescriptor).uid ?? (poData as IOldTaskDescriptor)._id];
		}
	}

	/** @override */
	public override async compactAsync(): Promise<void> {
		return Promise.resolve();
	}

	/** @override */
	public override migrateSchemaAsync(): Promise<void> {
		return Promise.resolve();
	}

	//#region Init

	/** @override */
	protected override async initAsync(): Promise<void> {
		return new Promise<void>((pfResolve, pfReject) => {
			const loOpenDbRequest: IDBOpenDBRequest = indexedDB.open(BrowserBackgroundTaskDatabaseService.C_DATABASE_ID);

			loOpenDbRequest.onerror = (poEvent: Event) => {
				console.error(
					`${BrowserBackgroundTaskDatabaseService.C_LOG_ID}Error while creating bts database for '${BrowserBackgroundTaskDatabaseService.C_DATABASE_ID}`,
					(poEvent.target as IDBOpenDBRequest).error
				);
				this.raiseDatabaseError(loOpenDbRequest.error);
				pfReject();
			};

			loOpenDbRequest.onsuccess = (poEvent: Event) => {
				this.raiseDatabaseEvent((poEvent.target as IDBOpenDBRequest).result);
				pfResolve();
			};

			loOpenDbRequest.onupgradeneeded = (poEvent: Event) => {
				// https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API/Using_IndexedDB#creating_or_updating_the_version_of_the_database
				(poEvent.target as IDBOpenDBRequest).result.createObjectStore(
					BrowserBackgroundTaskDatabaseService.C_DESCRIPTOR_TABLE_NAME,
					{ keyPath: BrowserBackgroundTaskDatabaseService.C_DESCRIPTOR_TABLE_PRIMARY_KEY }
				);
			};
		});
	}

	//#endregion Init

	//#region Put

	/** @override */
	public override putAsync(poDocument: ITaskDescriptor | IOldTaskDescriptor): Promise<void> {
		return this.transactAsync(ETransactionAction.put, poDocument);
	}

	//#endregion Put

	//#region Get

	/** @override */
	public override async getAsync(psId: string): Promise<ITaskDescriptor | undefined> {
		const loTransaction: IDBTransaction = await this.getNewTransactionAsync("readonly");

		return new Promise<ITaskDescriptor | undefined>((pfResolve, pfReject) => {
			loTransaction.onerror = (poEvent: Event) => {
				console.error(
					`${BrowserBackgroundTaskDatabaseService.C_LOG_ID}Error get task descriptor '${psId}'`,
					(poEvent.target as IDBTransaction).error
				);
				pfReject((poEvent.target as IDBTransaction).error);
			};

			const loRequest: IDBRequest = loTransaction
				.objectStore(BrowserBackgroundTaskDatabaseService.C_DESCRIPTOR_TABLE_NAME)
				.get(psId);

			loRequest.onsuccess = (poEvent: Event) => {
				const loSerializedDescriptor: ISerializableTaskDescriptor | undefined = (poEvent.target as IDBRequest).result;
				pfResolve(loSerializedDescriptor ? this.toTaskDescriptor(loSerializedDescriptor) : undefined);
			};
		});
	}

	public override async getAllAsync(): Promise<ITaskDescriptor[]> {
		const loTransaction: IDBTransaction = await this.getNewTransactionAsync("readonly");

		return new Promise<ITaskDescriptor[]>((pfResolve, pfReject) => {
			loTransaction.onerror = (poEvent: Event) => {
				console.error(
					`${BrowserBackgroundTaskDatabaseService.C_LOG_ID}Error get all task descriptors :`,
					(poEvent.target as IDBTransaction).error
				);
				pfReject((poEvent.target as IDBTransaction).error);
			};

			const loRequest: IDBRequest = loTransaction
				.objectStore(BrowserBackgroundTaskDatabaseService.C_DESCRIPTOR_TABLE_NAME)
				.getAll();

			loRequest.onsuccess = (poEvent: Event) => {
				const laSerializedDescriptors: ISerializableTaskDescriptor[] | undefined = (poEvent.target as IDBRequest)
					.result;

				if (ArrayHelper.hasElements(laSerializedDescriptors)) {
					pfResolve(laSerializedDescriptors.map((poDoc: ISerializableTaskDescriptor) => this.toTaskDescriptor(poDoc)));
				} else pfResolve([]);
			};
		});
	}

	//#endregion Get

	//#region Delete

	/** @override */
	public override deleteAsync(psId: string): Promise<void>;
	/** @override */
	public override deleteAsync(poDescriptor: ITaskDescriptor | IOldTaskDescriptor): Promise<void>;
	public override deleteAsync(poData: string | (ITaskDescriptor | IOldTaskDescriptor)): Promise<void> {
		return this.transactAsync(ETransactionAction.delete, poData);
	}

	public override deleteMultipleAsync(paIds: string[]): Promise<void>;
	public override deleteMultipleAsync(paDescriptors: ITaskDescriptor[] | IOldTaskDescriptor[]): Promise<void>;
	public override deleteMultipleAsync(paData: string[] | (ITaskDescriptor[] | IOldTaskDescriptor[])): Promise<void> {
		return this.transactAsync(ETransactionAction.deleteMultiple, paData);
	}

	//#endregion Delete

	//#endregion METHODS
}
